<script setup>
import { computed, onMounted, ref } from 'vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import MetaBusinessAccountsAPI from '../../../../api/metaBusinessAccounts';

const connections = ref([]);
const assetsByConnection = ref({});
const analytics = ref({});
const conversionEvents = ref([]);
const eventMappings = ref([]);
const isLoading = ref(false);
const isConnecting = ref(false);
const hasConnectError = ref(false);

const hasConnections = computed(() => connections.value.length > 0);

const redirectUri = computed(
  () => `${window.location.origin}/meta_business_accounts/callback`
);

const fetchAssets = async id => {
  const response = await MetaBusinessAccountsAPI.assets(id);
  assetsByConnection.value = {
    ...assetsByConnection.value,
    [id]: response.data || [],
  };
};

const fetchAll = async () => {
  isLoading.value = true;
  try {
    const connectionsResponse = await MetaBusinessAccountsAPI.get();
    connections.value = connectionsResponse.data || [];

    if (!hasConnections.value) {
      assetsByConnection.value = {};
      analytics.value = {};
      conversionEvents.value = [];
      eventMappings.value = [];
      return;
    }

    const [analyticsResponse, eventsResponse, mappingsResponse] =
      await Promise.all([
        MetaBusinessAccountsAPI.analytics(),
        MetaBusinessAccountsAPI.conversionEvents(),
        MetaBusinessAccountsAPI.eventMappings(),
      ]);

    analytics.value = analyticsResponse.data || {};
    conversionEvents.value = eventsResponse.data || [];
    eventMappings.value = mappingsResponse.data || [];

    await Promise.all(
      connections.value.map(connection => fetchAssets(connection.id))
    );
  } finally {
    isLoading.value = false;
  }
};

const connect = async () => {
  hasConnectError.value = false;
  isConnecting.value = true;
  try {
    const response = await MetaBusinessAccountsAPI.oauthUrl(redirectUri.value);
    window.location.assign(response.data.url);
  } catch {
    hasConnectError.value = true;
  } finally {
    isConnecting.value = false;
  }
};

const syncAssets = async id => {
  await MetaBusinessAccountsAPI.syncAssets(id);
};

const toggleAsset = async (connectionId, asset) => {
  const assets = assetsByConnection.value[connectionId] || [];
  const selectedIds = assets
    .filter(item => (item.id === asset.id ? !item.connected : item.connected))
    .map(item => item.id);
  const response = await MetaBusinessAccountsAPI.updateAssets(
    connectionId,
    selectedIds
  );
  assetsByConnection.value = {
    ...assetsByConnection.value,
    [connectionId]: response.data || [],
  };
};

onMounted(fetchAll);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('META_BUSINESS_ACCOUNTS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('META_BUSINESS_ACCOUNTS.HEADER')"
        :description="$t('META_BUSINESS_ACCOUNTS.DESCRIPTION')"
        feature-name="meta_business_accounts"
      />
    </template>

    <template #body>
      <div
        v-if="!hasConnections"
        class="flex flex-col items-start gap-3 overflow-auto"
      >
        <button
          type="button"
          class="button button--primary"
          :disabled="isConnecting"
          @click="connect"
        >
          {{
            isConnecting
              ? $t('META_BUSINESS_ACCOUNTS.CONNECTING')
              : $t('META_BUSINESS_ACCOUNTS.CONNECT')
          }}
        </button>
        <p v-if="hasConnectError" class="text-sm text-n-ruby-9">
          {{ $t('META_BUSINESS_ACCOUNTS.CONNECT_ERROR') }}
        </p>
      </div>

      <div v-else class="flex flex-col gap-6 overflow-auto">
        <section class="flex flex-col gap-3">
          <div class="flex items-center justify-between gap-3">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('META_BUSINESS_ACCOUNTS.CONNECTIONS') }}
            </h3>
            <button
              type="button"
              class="button button--secondary"
              :disabled="isConnecting"
              @click="connect"
            >
              {{ $t('META_BUSINESS_ACCOUNTS.RECONNECT') }}
            </button>
          </div>
          <div
            v-for="connection in connections"
            :key="connection.id"
            class="border border-n-weak rounded-lg p-4 flex flex-col gap-4"
          >
            <div class="flex items-center justify-between gap-3">
              <div>
                <div class="font-medium text-n-slate-12">
                  {{ connection.business_name || connection.business_id }}
                </div>
                <div class="text-sm text-n-slate-11">
                  {{ connection.status }}
                  {{ $t('META_BUSINESS_ACCOUNTS.SEPARATOR') }}
                  {{
                    connection.token_health?.status ||
                    $t('META_BUSINESS_ACCOUNTS.UNKNOWN_HEALTH')
                  }}
                </div>
              </div>
              <button
                type="button"
                class="button button--secondary"
                @click="syncAssets(connection.id)"
              >
                {{ $t('META_BUSINESS_ACCOUNTS.SYNC') }}
              </button>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
              <label
                v-for="asset in assetsByConnection[connection.id] || []"
                :key="asset.id"
                class="flex items-center gap-3 border border-n-weak rounded-md p-3"
              >
                <input
                  type="checkbox"
                  :checked="asset.connected"
                  @change="toggleAsset(connection.id, asset)"
                />
                <span class="min-w-0">
                  <span
                    class="block font-medium text-sm text-n-slate-12 truncate"
                  >
                    {{ asset.name || asset.asset_id }}
                  </span>
                  <span class="block text-xs text-n-slate-11">
                    {{ asset.asset_type }}
                  </span>
                </span>
              </label>
            </div>
          </div>
        </section>

        <section class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div class="border border-n-weak rounded-lg p-4">
            <div class="text-sm text-n-slate-11">
              {{ $t('META_BUSINESS_ACCOUNTS.CONVERSATIONS_FROM_ADS') }}
            </div>
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ analytics.totals?.conversations_from_ads || 0 }}
            </div>
          </div>
          <div class="border border-n-weak rounded-lg p-4">
            <div class="text-sm text-n-slate-11">
              {{ $t('META_BUSINESS_ACCOUNTS.SENT_CONVERSIONS') }}
            </div>
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ analytics.totals?.sent_conversions || 0 }}
            </div>
          </div>
          <div class="border border-n-weak rounded-lg p-4">
            <div class="text-sm text-n-slate-11">
              {{ $t('META_BUSINESS_ACCOUNTS.FAILED_CONVERSIONS') }}
            </div>
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ analytics.totals?.failed_conversions || 0 }}
            </div>
          </div>
        </section>

        <section class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div class="border border-n-weak rounded-lg p-4">
            <h3 class="text-base font-medium text-n-slate-12 mb-3">
              {{ $t('META_BUSINESS_ACCOUNTS.EVENT_MAPPINGS') }}
            </h3>
            <div
              v-for="mapping in eventMappings"
              :key="mapping.id"
              class="flex justify-between py-2 border-b border-n-weak last:border-b-0"
            >
              <span>{{ mapping.internal_event_name }}</span>
              <span class="text-n-slate-11">{{ mapping.meta_event_name }}</span>
            </div>
          </div>

          <div class="border border-n-weak rounded-lg p-4">
            <h3 class="text-base font-medium text-n-slate-12 mb-3">
              {{ $t('META_BUSINESS_ACCOUNTS.CONVERSION_EVENTS') }}
            </h3>
            <div
              v-for="event in conversionEvents"
              :key="event.id"
              class="flex justify-between py-2 border-b border-n-weak last:border-b-0"
            >
              <span>{{ event.event_name }}</span>
              <span class="text-n-slate-11">{{ event.status }}</span>
            </div>
          </div>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
